import parser from 'ua-parser-js'
import { stringify } from 'qs'
import Index from 'components/pages/Index'
import MetaTags from 'components/pages/Index/MetaTags'
import withLivingSinyi from 'utils/withLivingSinyi'
import { lsGet, safeAwait } from 'utils/request'
import {
  getServiceClassifications,
  getBanners,
  getArticleLayout,
  getLayoutModules,
} from 'utils/api'
import NoindexRemoval from 'components/common/NoindexRemoval'

const IndexPage = (props) => {
  return (
    <>
      <NoindexRemoval />
      <MetaTags
        banner={props.fallbackData?.banners?.[0]}
        isMobile={props.isMobile}
      />
      <Index {...props} />
    </>
  )
}

export const getServerSideProps = async (context) => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=3600, stale-while-revalidate=3600'
  )

  const ua = parser(context.req.headers['user-agent'])
  const isMobile = ['mobile', 'tablet'].includes(ua.device.type)

  try {
    const headerMenuApiKey = `/catalogs/serviceMenus?${stringify({
      target: 'headerMenu',
    })}`
    const mainHomeServiceMenuApiKey = `/catalogs/serviceMenus?${stringify({
      target: 'mainHomeServiceMenu',
    })}`

    const [
      generalClassificationsResult,
      bannersResult,
      articleLayoutResult,
      homePageLayoutModule,
      headerMenusResult,
      mainHomeServiceMenusResult,
    ] = await Promise.all([
      getServiceClassifications({ mode: 'general', displayVisible: 'visible' }),
      getBanners(context.query?.webview),
      getArticleLayout('homepages'),
      getLayoutModules({ type: 'homepages', platform: 'web' }),
      safeAwait(lsGet(headerMenuApiKey)),
      safeAwait(lsGet(mainHomeServiceMenuApiKey)),
    ])

    const [generalClassifications, error] = generalClassificationsResult
    if (error) {
      throw new Error(
        `Failed to fetch service classifications: ${error.message}`
      )
    }

    const [banners, bannersError] = bannersResult
    if (bannersError) {
      throw new Error(`Failed to fetch banners: ${bannersError.message}`)
    }

    const [articleLayout, articleLayoutError] = articleLayoutResult
    if (articleLayoutError) {
      throw new Error(
        `Failed to fetch articleLayout: ${articleLayoutError.message}`
      )
    }

    const [homePageLayout, homePageLayoutError] = homePageLayoutModule
    if (homePageLayoutError) {
      throw new Error(
        `Failed to fetch homePageLayout: ${homePageLayoutError.message}`
      )
    }

    const [headerMenus] = headerMenusResult
    const [mainHomeServiceMenus] = mainHomeServiceMenusResult

    // 將 SSR 資料的 API 路徑存在 SWR fallback，當元件 call 這些 API 時可直接使用 SSR 資料進行初始化，避免 Prop Drilling
    const swrFallback = {
      [headerMenuApiKey]: headerMenus ?? null,
      [mainHomeServiceMenuApiKey]: mainHomeServiceMenus ?? null,
    }

    return {
      props: {
        isMobile,
        swrFallback,
        fallbackData: {
          generalClassifications: generalClassifications.map(
            (classification) => ({
              mode: classification.mode,
              name: classification.name,
              identityCode: classification.identityCode,
              _id: classification._id,
            })
          ),
          banners: banners.map((banner) => ({
            navData: banner.navData,
            image: banner.image,
            imageDouble: banner.imageDouble,
            _id: banner._id,
            name: banner.name,
          })),
          articleLayout: articleLayout || null,
          homePageLayout: homePageLayout || null,
        },
      },
    }
  } catch (err) {
    const isAdReviewVisit = require('utils/server-utils/is-ad-review-visit')
    if (!isAdReviewVisit(context.req, context.query)) {
      throw err
    }
    // 首頁是其他頁面錯誤時的 fallback 目標，絕不能 redirect（會造成迴圈）：
    // 對廣告審核器改用空資料降級渲染，維持 200
    context.res.setHeader('Cache-Control', 'no-cache')
    const logger = require('utils/server-utils/logger')
    const apm = require('elastic-apm-node')
    logger.warn({
      event: 'gssp_fallback_degraded',
      page: context.resolvedUrl,
      reason: err?.message,
      ...(apm.isStarted() ? apm.currentTraceIds : {}),
    })
    if (apm.isStarted()) {
      apm.captureError(err, {
        custom: { page: context.resolvedUrl, gsspFallback: true },
      })
    }
    return {
      props: {
        isMobile,
        swrFallback: {},
        fallbackData: {
          generalClassifications: [],
          banners: [],
          articleLayout: null,
          homePageLayout: null,
        },
      },
    }
  }
}

export default withLivingSinyi(IndexPage)
